/**
 * Content model for the "unsure" trial dashboard (TTFV Story 4) — the home for
 * trial users who have not decided between keeping their EHR and replacing it.
 * Its whole job is to present those two paths side by side and make either one
 * immediately explorable, so the page is deliberately exploratory rather than a
 * guided sequence.
 *
 * Lives in its own file for the same reason `EhrTrialDashboard.type.ts` does:
 * it is a whole page's worth of copy. `TrialHomeContent.unsureDashboard` is the
 * single key that hangs it off the stored `trialHome` AppData row.
 *
 * What is NOT here, on purpose:
 *
 *  - The three clinical-tool cards. They come from `TrialHomeContent.cards`
 *    (section `essentials`), the same list the card-based trial home renders,
 *    so the tools have one set of copy and one set of click behaviors.
 *  - The workflow's videos and report list. Those come from the `ehrDashboard`
 *    block (`scheduleVideoUrl`, `billingVideoUrl`, `reportingItems`,
 *    `videoWatchThresholdSeconds`). Scheduling here and Scheduling on the EHR
 *    dashboard are the same product experience; configuring it twice would let
 *    the two dashboards drift on whichever one an admin forgot to update.
 *
 * So this type holds copy only. Every string is admin-editable in
 * chiroup-admin's Trial Experience editor.
 *
 * SAOP is deliberately spelled S-A-O-P throughout. It is not a typo for SOAP.
 */
export type UnsureTrialDashboardContent = {
  /* Dashboard introduction — informational, no CTA (spec section 4). */
  introHeading: string;
  introCopy: string;

  /* Existing EHR / clinical tools — the green path (spec section 5). */
  toolsHeading: string;
  toolsSupportingCopy: string;
  /**
   * Button label shared by all three tool cards, rendered with a trailing
   * arrow by the page. One label rather than three because the cards are the
   * shared `essentials` cards, which carry no CTA field of their own.
   */
  toolsCta: string;

  /* Complete EHR — the orange path (spec section 6). */
  ehrHeading: string;
  ehrSupportingHeadline: string;
  /** Short benefit lines listed beside the workflow. */
  ehrBenefits: string[];
  /**
   * Workflow node labels and their actions, in render order: Scheduling →
   * Charting (SAOP) → Billing → Reporting. Each step is independently
   * actionable; the connectors between them are a picture of how the practice
   * flows, not a stepper the user has to walk in order.
   */
  schedulingTitle: string;
  schedulingCta: string;
  chartingTitle: string;
  chartingCta: string;
  billingTitle: string;
  billingCta: string;
  reportingTitle: string;
  reportingCta: string;

  /* SAOP feature highlight (spec section 7). Its CTA opens the same sandbox
     the Charting workflow step does — two entry points, one experience. */
  saopHeading: string;
  saopCopy: string;
  saopCta: string;
  /**
   * Secondary "Why SAOP?" CTA beside it (Kara, 2026-09-28). Label only: the
   * clip it plays is the EHR dashboard's `saopVideoUrl`, shared on purpose so
   * both dashboards show one SAOP story, and the button stays hidden until
   * that URL is configured.
   */
  whySaopCta: string;

  /* Schedule a walkthrough — the blue path (spec section 8). */
  walkthroughHeading: string;
  walkthroughCopy: string;
  walkthroughCta: string;
};

export const DEFAULT_UNSURE_TRIAL_DASHBOARD: UnsureTrialDashboardContent = {
  introHeading: 'See what ChiroUp can do for your practice.',
  introCopy:
    "Start with whatever interests you, whether that's simple tools that plug into your existing system or a whole new EHR.",

  toolsHeading: 'Improve patient care with the system you already use.',
  toolsSupportingCopy: 'Powerful clinical tools that work alongside any EHR.',
  toolsCta: 'Explore',

  ehrHeading: 'Ready for a whole new EHR?',
  ehrSupportingHeadline: 'A smarter way to run your entire practice.',
  ehrBenefits: [
    'Fewer clicks.',
    'Seamless workflows.',
    'More automation.',
    'Powerful AI built in.',
  ],
  schedulingTitle: 'Scheduling',
  schedulingCta: 'See scheduling',
  chartingTitle: 'Charting (SAOP)',
  chartingCta: 'Try a note',
  billingTitle: 'Billing',
  billingCta: 'See billing',
  reportingTitle: 'Reporting',
  reportingCta: 'See reporting',

  saopHeading: 'It starts with SAOP.',
  saopCopy: 'See how ChiroUp turns your diagnosis into a faster, more complete note.',
  saopCta: 'Try a SAOP note',
  whySaopCta: 'Why SAOP?',

  walkthroughHeading: 'Get the most out of your trial.',
  walkthroughCopy:
    'Schedule a friendly walkthrough with one of our specialists to help you get the most out of ChiroUp.',
  walkthroughCta: 'Schedule a walkthrough',
};

/**
 * Hydrate a stored `unsureDashboard` block. Mirrors
 * `mergeEhrDashboardWithDefaults`: a MISSING field falls back to the default
 * rather than landing as `undefined`.
 *
 * A stored `ehrBenefits` array is respected verbatim, including an empty one:
 * an admin deleting every row means exactly that, and the deletion must
 * survive reloads (Kara, 2026-09-28 — emptied lists kept resurrecting their
 * defaults). The editor loads merged content, so an untouched list
 * round-trips the default values, never `[]`.
 */
export function mergeUnsureDashboardWithDefaults(
  stored: Partial<UnsureTrialDashboardContent> | null | undefined
): UnsureTrialDashboardContent {
  if (!stored) return DEFAULT_UNSURE_TRIAL_DASHBOARD;

  return {
    ...DEFAULT_UNSURE_TRIAL_DASHBOARD,
    ...stored,
    ehrBenefits: stored.ehrBenefits ?? DEFAULT_UNSURE_TRIAL_DASHBOARD.ehrBenefits,
  };
}
